import type {
  AccessListItem,
  AccessListName,
  AccessRowTypes,
} from '@app/application/user-management/types'
import type { AccessListView } from '@app/application/user-management/utils/access-lists'
import { computed, reactive } from 'vue'

import { accessCount } from '@app/application/user-management/utils/access-lists'
import { useUserPanelStore } from '@app/application/user-management/stores/user-panel'

// Everything an `AccessList` reads from and sends to the store for one list.
// It is reactive, so a template reads `binding.items` without `.value`.
export interface AccessListBinding<L extends AccessListName> {
  items: AccessListItem[]
  paging: AccessListView<AccessRowTypes[L]>
  count: number | string
  search: (query: string) => Promise<void>
  loadMore: () => Promise<void>
}

export default function useAccessList<L extends AccessListName>(
  name: L,
  toItems: (rows: AccessRowTypes[L][]) => AccessListItem[]
): AccessListBinding<L> {
  const store = useUserPanelStore()
  const paging = computed(() => store.accessViews[name])

  return reactive({
    items: computed(() => toItems(paging.value.rows)),
    paging,
    count: computed(() => accessCount(paging.value)),
    search: (query: string) => store.searchAccess(name, query),
    loadMore: () => store.loadMoreAccess(name),
  })
}
